import React, { useState, lazy, Suspense } from 'react';

import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';
import { Space } from 'components/barbr';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import Container from 'react-bootstrap/Container';
import { HelpIcon, UserIcon, DiamondIcon, DashboardIcon } from 'components/Icon';

import { initials } from '_dash';
import { LOGIN_ROUTE, MY_BOOKINGS_ROUTE } from 'appRoutes/routeConstants';
import { TIKTOK_URL, INSTAGRAM_URL, LANDING_PAGE_URL } from 'global_constants';
import { ReactComponent as MenuSvg } from 'assets/hamburger.svg';
import { ReactComponent as HelpMobile } from 'assets/helpMobile.svg';
import RefererItem from 'components/refererItem';
import logo from 'assets/logo.svg';
import whiteLogo from 'assets/white-logo.svg';
import tiktokIcon from 'assets/icons/tiktok-color.png';
import instagramIcon from 'assets/icons/instagram-color.png';
import { useHelpCrunch } from 'context/helpCrunchContext';
import { getBarberBookingSlotRoute, getProfileRoute } from 'appRoutes/routeHelpers';
import AvatarText from 'components/barbr/AvatarText';

import './style.scss';

const IncompatibleService = lazy(() => import('../../components/cart/component/incompatibleService'));
const AccountSuspendedModal = lazy(() => import('../../../../../components/accountSuspendedModal'));

const navItems = [
  {
    isLoginRoute: true,
    label: 'My bookings',
    path: MY_BOOKINGS_ROUTE,
    icon: <DiamondIcon size={26} />,
  },
  {
    label: 'Get help',
    url: '',
    icon: <HelpIcon size={26} />,
  },
  {
    label: 'Set up your business',
    url: LANDING_PAGE_URL,
    icon: <DashboardIcon size={26} />,
  },
];

const MobileProfileNavBar = React.memo(({ detail, isLoggedIn, accountData, onLogout, services }) => {
  const history = useHistory();
  const { username } = detail;
  const { isHelpCrunchReady } = useHelpCrunch();

  const [isExpanded, setIsExpanded] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isAccountSuspended, setAccountSuspended] = useState(false);

  const clientReferral = detail.client_referral || {};

  const redirectToLogin = () => {
    history.push({
      pathname: LOGIN_ROUTE,
      state: { username: username },
      search: `?next=${getProfileRoute(username)}`,
    });
  };

  const onRouteClick = (item) => {
    const { path, isLoginRoute } = item;
    if (!path) {
      onHelp();
      return;
    }
    if (isLoginRoute && isLoggedIn) {
      history.push({
        pathname: path,
        search: `?next=${getProfileRoute(username)}`,
      });
    } else {
      redirectToLogin();
    }
  };

  const onHelp = () => {
    if (isHelpCrunchReady) {
      window?.HelpCrunch('openChat');
    }
  };

  const closeAccountSuspended = () => setAccountSuspended(false);

  const onClickBook = () => {
    if (detail?.is_account_suspended) {
      setAccountSuspended(true);
      return;
    }
    const locationTypes = services.map((item) => item.location_type);
    if (locationTypes.every((type) => type === 'shop' || type === 'mobile' || type === 'custom')) {
      // All objects have location_type as "shop" or "mobile"
      if (locationTypes.includes('custom') && locationTypes.includes('shop')) {
        setVisible(true);
      } else {
        history.push(getBarberBookingSlotRoute(username));
      }
    } else if (locationTypes.includes('custom') && locationTypes.includes('shop')) {
      // Some objects have location_type as "custom" and some have "shop"
      setVisible(true);
    }
  };

  const onModalDropBackClose = () => setVisible(false);

  const closeIncompatibleServiceModal = () => setVisible(false);

  return (
    <>
      <Navbar
        fixed='top'
        expand='lg'
        bg={isExpanded ? 'light' : 'dark'}
        onToggle={(value) => setIsExpanded(value)}
        className={`profile-navbar ${isExpanded ? 'navbar-expanded' : 'navbar-collapsed'}`}>
        <Container className='navbar-container'>
          <Navbar.Toggle as='div'>
            <MenuSvg />
          </Navbar.Toggle>

          <div className='helpWrapper' onClick={onHelp}>
            <HelpMobile />
          </div>

          <Container className='brand-container'>
            <Navbar.Brand href='#'>
              <img alt='barbr' width={78} height={20} src={isExpanded ? logo : whiteLogo} className={isExpanded ? 'expanded' : 'collapsed'} />
            </Navbar.Brand>
          </Container>

          <If condition={Object.keys(services).length && !isExpanded}>
            <Navbar.Brand href='#' className='book-button'>
              <button size='sm' onClick={onClickBook}>
                Book
              </button>
            </Navbar.Brand>
          </If>

          <If condition={isExpanded}>
            <Space className='auth-icon' align='center' size={8}>
              <Choose>
                <When condition={isLoggedIn}>
                  <p onClick={onLogout} className='text-normal semi-bold'>
                    Log out
                  </p>
                  <AvatarText text={initials(accountData.full_name)} />
                </When>
                <Otherwise>
                  <Space size={8} align='center' onClick={redirectToLogin}>
                    <p className='text-xl semi-bold'>Log in</p>
                    <UserIcon />
                  </Space>
                </Otherwise>
              </Choose>
            </Space>
          </If>
        </Container>
        <Navbar.Collapse>
          <RefererItem username={username} isLoggedIn={isLoggedIn} referrral={clientReferral} />
          <Container>
            <Nav>
              {navItems.map((item, idx) => {
                return (
                  <Choose>
                    <When condition={item.url}>
                      <Nav.Link href={item.url} key={idx}>
                        {item.icon}
                        <p>{item.label}</p>
                      </Nav.Link>
                    </When>
                    <Otherwise>
                      <Nav.Link key={idx} onClick={() => onRouteClick(item)}>
                        {item.icon}
                        <p>{item.label}</p>
                      </Nav.Link>
                    </Otherwise>
                  </Choose>
                );
              })}
            </Nav>
          </Container>
          <Container className='navbar-social'>
            <Space size={26} justify='justify-center'>
              <a href={INSTAGRAM_URL} target='_blank' rel='noreferrer'>
                <img src={instagramIcon} alt='Instagram' width={20} height={20} />
              </a>
              <a href={TIKTOK_URL} target='_blank' rel='noreferrer'>
                <img src={tiktokIcon} alt='Tiktok' width={20} height={20} />
              </a>
            </Space>
            <p className='copyright'>© {dayjs().format('YYYY')} Barbr Ltd. All rights reserved.</p>
          </Container>
        </Navbar.Collapse>
      </Navbar>
      <If condition={isExpanded}>
        <div className='profile-navbar-overlay' />
      </If>
      <Suspense fallback={<></>}>
        <IncompatibleService visible={visible} onModalDropBackClose={onModalDropBackClose} closeIncompatibleServiceModal={closeIncompatibleServiceModal} />
        <AccountSuspendedModal visible={isAccountSuspended} barberProfileId={detail.id} customerId={accountData.id} onClose={closeAccountSuspended} />
      </Suspense>
    </>
  );
});

export default MobileProfileNavBar;
